import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

import { GUTTER_PX } from '../../styles/tokens';
import { type ClampBounds, clampPct, getClampBounds } from './clampPct';

/** The separator's own width in px; also the default `gutterPx` prop value. Sourced
 *  from `styles/tokens.ts`'s `GUTTER_PX` -- the single source of truth, not
 *  a locally hard-coded copy. */
const DEFAULT_GUTTER_PX = GUTTER_PX;
/** ArrowLeft/ArrowRight keyboard step, in left-pane percentage points. */
const ARROW_STEP_PCT = 2;
/** Bounds shown on the very first render, before the container has been measured. The mount-time
 *  effect (`measureAndClamp`) replaces this with the real, rect-derived bounds as soon as it runs;
 *  `@testing-library/react`'s `render()` flushes effects before returning, so tests never observe
 *  this placeholder. */
const UNMEASURED_BOUNDS: ClampBounds = { min: 0, max: 100 };

export interface ResizableSplitProps {
  /** Minimum width (px) the left pane must keep. */
  leftMinPx: number;
  /** Minimum width (px) the right pane must keep. */
  rightMinPx: number;
  /** Starting left-pane percentage, subject to the mount-time clamp like any other value. */
  initialLeftPct: number;
  /** Separator width in px. Defaults to 8. */
  gutterPx?: number;
  left: React.ReactNode;
  right: React.ReactNode;
  /** Accessible name for the separator. Defaults to a generic label so it always has one. */
  ariaLabel?: string;
}

/** Each pane cell is a BOUNDED box (`minWidth`/`minHeight: 0` override the grid item's default
 *  `min-*: auto`, which would otherwise let the cell grow to fit its content and defeat clamping).
 *  `overflow: hidden` lives on the cell itself, not `overflow-y: auto` -- the cell only needs to
 *  stay bounded; a child that wants independent vertical scrolling supplies its own
 *  `overflow-y: auto` inside this bounded box and it will actually take effect (a child's
 *  `overflow-y: auto` has no constrained height to scroll within without a bounded ancestor). */
const paneStyle: React.CSSProperties = {
  minWidth: 0,
  minHeight: 0,
  height: '100%',
  overflow: 'hidden'
};

/**
 * Hand-rolled two-pane splitter. Local `pct` is EPHEMERAL UI layout state --
 * "no state management" targets business/domain/redux state, not this -- not persisted,
 * not business state.
 *
 * The gutter math itself lives in `./clampPct`, shared by the pointer handler, the keyboard
 * handler, and the rendered `aria-valuemin`/`now`/`max`, so all three always agree on the same
 * bounds instead of each re-deriving them.
 */
export const ResizableSplit: React.FC<ResizableSplitProps> = ({
  leftMinPx,
  rightMinPx,
  initialLeftPct,
  gutterPx = DEFAULT_GUTTER_PX,
  left,
  right,
  ariaLabel
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  /** Stable id for the LEFT/primary pane, referenced by the separator's `aria-controls` (WAI-ARIA
   *  window-splitter pattern: https://www.w3.org/WAI/ARIA/apg/patterns/windowsplitter/). */
  const leftPaneId = useId();

  const [pct, setPct] = useState(initialLeftPct);
  const [bounds, setBounds] = useState<ClampBounds>(UNMEASURED_BOUNDS);

  /** Measures the container and re-clamps both `bounds` and `pct` against it. `setBounds` always
   *  commits a freshly-computed object -- even when min/max didn't numerically change -- which is
   *  what guarantees a re-render happens right after mount even when `pct` was already in range
   *  (a same-value `setPct` call alone would let React bail out of re-rendering, leaving
   *  `UNMEASURED_BOUNDS` on screen). */
  const measureAndClamp = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return;
    const nextBounds = getClampBounds(rect, leftMinPx, rightMinPx, gutterPx);
    setBounds(nextBounds);
    setPct((prev) => Math.min(Math.max(prev, nextBounds.min), nextBounds.max));
  }, [leftMinPx, rightMinPx, gutterPx]);

  // Clamp on mount and whenever the mins/gutter change.
  useEffect(() => {
    measureAndClamp();
  }, [measureAndClamp]);

  // Re-clamp when the CONTAINER's own size changes. This must be a
  // ResizeObserver on the container, not just a `window.resize` listener -- a container can change
  // size (e.g. a sidebar toggling) without the window doing so.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(() => measureAndClamp());
    observer.observe(node);
    return () => observer.disconnect();
  }, [measureAndClamp]);

  // Safety-net for a drag that never receives a terminating pointer event. The
  // pointerup/pointercancel/lostpointercapture handlers below (all wired to `endDrag`) cover the
  // common ways a drag ends, INCLUDING the usual focus-loss path via `lostpointercapture`. But if the
  // WINDOW loses focus mid-drag (alt-tab, an OS focus steal, a devtools panel grabbing focus) WITHOUT
  // any of those firing on the separator, `draggingRef.current` would stay `true`, and the next
  // `pointermove` over the separator would resume resizing with no fresh `pointerdown`. Clearing the
  // ref on window `blur` closes that gap. A plain ref reset is enough here -- releasing pointer
  // capture is best-effort and the element may already have lost it -- so this deliberately does NOT
  // touch the existing pointer handlers; it is a completeness net matching their existing
  // pointercancel/lostpointercapture care.
  useEffect(() => {
    const handleWindowBlur = (): void => {
      if (draggingRef.current) {
        draggingRef.current = false;
      }
    };
    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      draggingRef.current = true;
    },
    []
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPct(clampPct(event.clientX, rect, leftMinPx, rightMinPx, gutterPx));
    },
    [leftMinPx, rightMinPx, gutterPx]
  );

  /** Ends an active drag and releases pointer capture if still held. Shared by `pointerup`,
   *  `pointercancel`, AND `lostpointercapture` (a touch/OS cancellation
   *  or an involuntary loss of capture must not leave a stale `draggingRef.current === true`,
   *  which would make the NEXT pointermove anywhere resume moving the separator without a new
   *  pointerdown). By the time `lostpointercapture` fires, capture is already gone, so the
   *  `hasPointerCapture` guard below just makes the release a no-op in that case -- the same
   *  function still correctly clears `draggingRef`. */
  const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    draggingRef.current = false;
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          setPct((prev) =>
            Math.min(Math.max(prev - ARROW_STEP_PCT, bounds.min), bounds.max)
          );
          break;
        case 'ArrowRight':
          event.preventDefault();
          setPct((prev) =>
            Math.min(Math.max(prev + ARROW_STEP_PCT, bounds.min), bounds.max)
          );
          break;
        case 'Home':
          event.preventDefault();
          setPct(bounds.min);
          break;
        case 'End':
          event.preventDefault();
          setPct(bounds.max);
          break;
        default:
          break;
      }
    },
    [bounds]
  );

  const halfGutter = gutterPx / 2;

  return (
    <div
      ref={containerRef}
      data-testid="resizable-split-root"
      style={{
        display: 'grid',
        gridTemplateColumns: `calc(${pct}% - ${halfGutter}px) ${gutterPx}px calc(${100 - pct}% - ${halfGutter}px)`,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        flex: 1
      }}
    >
      <div data-testid="resizable-split-left" id={leftPaneId} style={paneStyle}>
        {left}
      </div>
      {/* biome-ignore lint/a11y/useSemanticElements: this is the WAI-ARIA "window splitter"
          pattern (https://www.w3.org/WAI/ARIA/apg/patterns/windowsplitter/), a FOCUSABLE separator
          (tabIndex + aria-valuenow/min/max, keyboard-operable) -- <hr> cannot be focusable or
          carry a live aria-valuenow, so a generic element is required here. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={ariaLabel ?? 'Resize panes'}
        aria-controls={leftPaneId}
        aria-valuemin={bounds.min}
        aria-valuenow={pct}
        aria-valuemax={bounds.max}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        onKeyDown={handleKeyDown}
        style={{
          width: `${gutterPx}px`,
          height: '100%',
          cursor: 'col-resize',
          touchAction: 'none'
        }}
      />
      <div data-testid="resizable-split-right" style={paneStyle}>
        {right}
      </div>
    </div>
  );
};
